import React, { memo, useCallback, useEffect, useState } from 'react';
import Button from '@atlaskit/button/new';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { ColorPickerMenu } from '../color-picker/index.tsx';
import { OptionTag } from '../option-tag/index.tsx';
import { GREY_META } from '../color-picker/constants.tsx';
import messages from './messages.tsx';
import type { ColorOptionData } from './types.tsx';

export type Props = {
	onCancel: () => void;
	onSubmit: (data: ColorOptionData) => void;
	initialData: ColorOptionData;
	isNewLabel?: boolean;
};

export const LabelsColorPickerForm = memo<Props>(
	({ onCancel, onSubmit, initialData, isNewLabel = false }: Props) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { color, name } = initialData;
		const { formatMessage } = useIntl();
		const [currentColor, setCurrentColor] = useState<string>(!color ? GREY_META.name : color);

		useEffect(() => {
			fireTrackAnalytics(createAnalyticsEvent({}), 'LabelsColorPickerForm viewed', {
				isNewLabel,
			});
		}, [createAnalyticsEvent, isNewLabel]);

		const handleSubmit = useCallback(() => {
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'LabelsColorPickerFormSave', {
				isNewLabel,
			});

			onSubmit && onSubmit({ name, color: currentColor });
		}, [createAnalyticsEvent, isNewLabel, onSubmit, name, currentColor]);

		const handleCancel = useCallback(() => {
			onCancel();

			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'LabelsColorPickerFormCancel', {
				isNewLabel,
			});
		}, [onCancel, createAnalyticsEvent, isNewLabel]);

		const handleKeyDownEvent = useCallback(
			(event: KeyboardEvent) => {
				if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
					handleCancel();
				}
				if (event.key === 'Enter' || event.keyCode === 13) {
					handleSubmit();
					event.preventDefault();
				}
				if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
					event.stopPropagation();
				}
			},
			[handleCancel, handleSubmit],
		);

		useEffect(() => {
			if (typeof window !== 'undefined') {
				window.addEventListener('keydown', handleKeyDownEvent, true);

				return () => {
					window.removeEventListener('keydown', handleKeyDownEvent, true);
				};
			}
		}, [handleKeyDownEvent]);

		return (
			<Box xcss={[formStyles, maxWidthStyles]} backgroundColor="elevation.surface.overlay">
				<Box xcss={formContentStyles}>
					<Box xcss={colorPickerWrapperStyles}>
						<ColorPickerMenu
							onChangeColor={(newColor: string) => {
								setCurrentColor(newColor);
								fireUIAnalytics(
									createAnalyticsEvent({}),
									'color clicked',
									'LabelsFormColorPicker',
									{
										color: newColor,
										isNewLabel,
									},
								);
							}}
							color={currentColor}
							colorPaletteVariant="new"
							colorCardVariant="outline"
						/>
					</Box>
					<Flex justifyContent="space-between" xcss={buttonContainerStyles}>
						<Box xcss={previewStyles}>
							<OptionTag name={name} color={currentColor} />
						</Box>
						<Button
							onBlur={(e: React.FocusEvent<HTMLButtonElement>) => {
								if (!e.relatedTarget) {
									e.stopPropagation();
								}
							}}
							appearance="primary"
							spacing="compact"
							testId="option-color-picker.labels-color-picker.submit-button"
							onClick={handleSubmit}
						>
							{formatMessage(messages.saveButton)}
						</Button>
					</Flex>
				</Box>
			</Box>
		);
	},
);

const formStyles = xcss({
	borderRadius: 'radius.small',
	boxShadow: 'elevation.shadow.overlay',
	display: 'block',
	padding: 'space.200',
	width: 'fit-content',
});
const maxWidthStyles = xcss({
	maxWidth: '356px',
});

const colorPickerWrapperStyles = xcss({
	marginBottom: 'space.150',
});

const formContentStyles = xcss({
	marginTop: 'space.100',
});

const previewStyles = xcss({
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	maxWidth: '180px',
});

const buttonContainerStyles = xcss({
	marginLeft: 'space.050',
	marginRight: 'space.050',
});
